import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";
import invoiceIcon from "../../assets/invoice-icon.png";

/* ================= REUSABLE SEARCH BAR ================= */
const SearchBar = ({ value, onChange, placeholder = "Search here..." }) => (
  <div style={styles.searchContainer}>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={styles.searchInput}
    />
  </div>
);

const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  profit: "#16A34A",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const Sales = () => {
  const { role, user } = useAuth();

  const [sales, setSales] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  // ADD THESE STATES (inside component, near searchTerm)
const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [partySearch, setPartySearch] = useState("");
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState([]); 
  const [activeProductIndex, setActiveProductIndex] = useState(null);

  const [showQuickParty, setShowQuickParty] = useState(false);
  const [quickParty, setQuickParty] = useState({ name: "", phone: "", type: "customer", balance: "" });

  const drawerRef = useRef(null);
  const partyDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  const initialForm = {
    bill_no: "",
    party_id: "",
    sale_date: new Date().toISOString().slice(0, 10),
    paid_amount: "",
    discount_percent: 0,
    round_off: 0,
    items: [{ product_id: "", qty: "", rate: "" }],
  };

  const [form, setForm] = useState(initialForm);

  const loadData = async () => {
    try {
      const saleRes = await api.get(`/sales/list.php?user_id=${user?.id}&role=${role}`);
      const partyRes = await api.get("/parties/list.php");
      const productRes = await api.get("/inventory/list.php");

      setSales(saleRes.data?.data || []);
      setParties(partyRes.data?.data || []);
      setProducts(productRes.data?.data || []);
    } catch (err) {
      console.error("Data load failed", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && drawerRef.current && !drawerRef.current.contains(e.target)) {
        if (!showQuickParty) resetForm();
      }
      if (showPartyDropdown && partyDropdownRef.current && !partyDropdownRef.current.contains(e.target)) {
        setShowPartyDropdown(false);
      }
      if (activeProductIndex !== null && productDropdownRef.current && !productDropdownRef.current.contains(e.target)) {
        setActiveProductIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm, showQuickParty, showPartyDropdown, activeProductIndex]);

  const generateBillNo = () => {
    const year = new Date().getFullYear();
    if (!sales.length) return `${year}-001`;
    const last = sales[0]?.bill_no || `${year}-000`;
    const lastNumber = parseInt(last.split("-")[1] || "0", 10);
    const nextNumber = String(lastNumber + 1).padStart(3, "0");
    return `${year}-${nextNumber}`;
  };

  useEffect(() => {
    if (showForm && !editingId) {
      setForm((prev) => ({ ...prev, bill_no: generateBillNo() }));
    }
  }, [showForm, sales]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError("");
    setForm(initialForm);
    setShowQuickParty(false);
    setPartySearch("");
    setProductSearch([]);
    setActiveProductIndex(null);
    setQuickParty({ name: "", phone: "", type: "customer", balance: "" });
  };

  const handleQuickPartySave = async () => {
    if (!quickParty.name.trim()) return alert("Party name is required");
    try {
      await api.post("/parties/create.php", {
        ...quickParty,
        balance: parseFloat(quickParty.balance) || 0
      });
      const partyRes = await api.get("/parties/list.php");
      const updatedParties = partyRes.data?.data || [];
      setParties(updatedParties);
      const newParty = updatedParties.find(p => p.name === quickParty.name);
      if(newParty) {
        setForm({ ...form, party_id: newParty.id });
        setPartySearch(newParty.name);
      }
      setShowQuickParty(false);
      setQuickParty({ name: "", phone: "", type: "customer", balance: "" });
    } catch (err) {
      alert("Failed to add party");
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...form.items];
    updated[index][field] = value;
    if (field === "product_id") {
      const selected = products.find((p) => String(p.id) === String(value));
      updated[index].rate = selected ? selected.price : "";
      const newSearch = [...productSearch];
      newSearch[index] = selected ? selected.name : "";
      setProductSearch(newSearch);
    }
    if (field === "qty") {
      const selected = products.find((p) => String(p.id) === String(updated[index].product_id));
      if (selected && parseFloat(value) > selected.current_stock) {
        alert("Only " + selected.current_stock + " items available in stock");
        updated[index].qty = selected.current_stock;
      }
    }
    setForm({ ...form, items: updated });
  };

  const addItemRow = () => {
    setForm({ ...form, items: [...form.items, { product_id: "", qty: "", rate: "" }] });
    setProductSearch([...productSearch, ""]);
  };

  const removeItemRow = (index) => {
    const updated = [...form.items];
    updated.splice(index, 1);
    const updatedSearch = [...productSearch];
    updatedSearch.splice(index, 1);
    setForm({ ...form, items: updated });
    setProductSearch(updatedSearch);
  };

  /* ================= CALCULATIONS WITH AUTO ROUND OFF ================= */
  const subtotal = form.items.reduce(
    (sum, item) => sum + (parseFloat(item.qty || 0) * parseFloat(item.rate || 0)),
    0
  );

  const discountAmount = (subtotal * parseFloat(form.discount_percent || 0)) / 100;
  const netAmount = subtotal - discountAmount;
  
  // Auto Round Off Logic: Math.round makes 100.4 -> 100 and 100.5 -> 101
  const grandTotal = Math.round(netAmount);
  const autoRoundOff = (grandTotal - netAmount).toFixed(2);

  const paid = parseFloat(form.paid_amount || 0);
  const selectedParty = parties.find((p) => String(p.id) === String(form.party_id));
  const partyBalance = Number(selectedParty?.balance || 0);
  const saleBalance = grandTotal - paid;
  const finalBalance = partyBalance + saleBalance;

  const filteredPartyDropdown = parties.filter(p => 
    p.name.toLowerCase().includes(partySearch.toLowerCase())
  );

const filteredSales = sales.filter((s) => {
  const matchSearch =
    s.bill_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.party_name?.toLowerCase().includes(searchTerm.toLowerCase());

  const saleDate = new Date(s.sale_date);

  const matchFrom = fromDate ? saleDate >= new Date(fromDate) : true;
  const matchTo = toDate ? saleDate <= new Date(toDate) : true;

  return matchSearch && matchFrom && matchTo;
});

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.bill_no || !form.party_id || form.items.length === 0) {
      setError("All fields required");
      return;
    }

    let newTab = window.open("", "_blank");

    try {
        const payload = {
            ...form,
            total: grandTotal,
            round_off: autoRoundOff, 
            user_id: user?.id,
            role: role
        };

      if (editingId) {
        await api.post("/sales/update.php", { id: editingId, ...payload });
        if (newTab) newTab.close();
      } else {
        const party = parties.find((p) => String(p.id) === String(form.party_id));
        let whatsappUrl = null;

        if (party?.phone) {
          let mobile = party.phone.toString().replace(/\D/g, "");
          if (mobile.startsWith("91") && mobile.length > 10) mobile = mobile.slice(-10);
          const message = `Hello ${party.name}, Bill No: ${form.bill_no}, Total: ₹${grandTotal}, Paid: ₹${paid.toFixed(2)}, Balance: ₹${saleBalance.toFixed(2)}. Thank you!`;
          whatsappUrl = `https://wa.me/91${mobile}?text=${encodeURIComponent(message)}`;
        }

        await api.post("/sales/create.php", payload);

        if (newTab && whatsappUrl) {
          newTab.location.href = whatsappUrl;
        } else if (newTab) {
          newTab.close();
        }
      }
      resetForm();
      loadData();
    } catch (err) {
      if (newTab) newTab.close();
      setError(err.response?.data?.error || "Error saving sale");
    }
  };

  const handleEdit = (sale) => {
    setForm({
      bill_no: sale.bill_no,
      party_id: sale.party_id,
      sale_date: sale.sale_date,
      paid_amount: sale.paid_amount,
      discount_percent: sale.discount_percent || 0,
      round_off: sale.round_off || 0,
      items: sale.items || [{ product_id: "", qty: "", rate: "" }],
    });
    setPartySearch(sale.party_name || "");
    const initialProductSearch = (sale.items || []).map(item => {
        const prod = products.find(p => String(p.id) === String(item.product_id));
        return prod ? prod.name : "";
    });
    setProductSearch(initialProductSearch);
    setEditingId(sale.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sale?")) return;
    try {
      await api.post("/sales/delete.php", { id });
      loadData();
    } catch {
      alert("Delete failed");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>Sales Management</h2>
          <p style={styles.subtitle}>Manage sales transactions</p>
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search by Bill No or Party..." 
          />
        </div>
 <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>

  {/* DATE FILTER */}
  <div style={{ display: "flex", gap: 8 }}>
    <input
      type="date"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
      style={styles.searchInput}
    />
    <input
      type="date"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
      style={styles.searchInput}
    />
  </div>

  <button style={styles.newBtn} onClick={() => setShowForm(true)}>
    + Add Sale
  </button>

  <button
    style={{ ...styles.newBtn, background: "#16A34A" }}
    onClick={() =>
      window.open(
        `https://codesaarthi.online/khatasaarthi/api/sales/export.php?from=${fromDate}&to=${toDate}`,
        "_blank"
      )
    }
  >
    Export Excel
  </button>
</div>
      </div>

      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Bill No</th>
                <th style={styles.th}>Party</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Disc%</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Paid</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((s, index) => (
                <tr key={s.id}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.tdBold}>{s.bill_no}</td>
                  <td style={styles.td}>{s.party_name}</td>
                  <td style={styles.td}>{s.sale_date}</td>
                  <td style={styles.td}>{s.discount_percent || 0}%</td>
                  <td style={styles.tdBold}>₹ {s.total}</td>
                  <td style={styles.td}>₹ {s.paid_amount}</td>
                  <td style={styles.td}>
                    {s.balance_amount > 0 ? (
                      <span style={styles.lowBadge}>₹ {s.balance_amount}</span>
                    ) : (
                      <span style={styles.okBadge}>₹ {s.balance_amount}</span>
                    )}
                  </td>
<td style={styles.actionCell}>
  <div style={styles.iconWrapper}>
    
    {/* INVOICE → sabko allowed */}
    <button
      style={styles.iconBtn}
      onClick={() =>
        window.open(
          `https://codesaarthi.online/khatasaarthi/api/sales/invoice.php?id=${s.id}`,
          "_blank"
        )
      }
    >
      <img src={invoiceIcon} alt="invoice" style={styles.icon} />
    </button>

    {/* EDIT → sabko allowed (including SALESMAN) */}
    <button
      style={styles.iconBtn}
      onClick={() => handleEdit(s)}
    >
      <img src={editIcon} alt="edit" style={styles.icon} />
    </button>

    {/* DELETE → only ADMIN */}
    {role === "ADMIN" && (
      <button
        style={styles.iconBtn}
        onClick={() => handleDelete(s.id)}
      >
        <img src={deleteIcon} alt="delete" style={styles.icon} />
      </button>
    )}

  </div>
</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && createPortal(
        <div style={styles.overlay}>
          <div ref={drawerRef} style={styles.drawer}>
            <div style={styles.drawerHeader}>
              <h2 style={styles.drawerTitle}>{editingId ? "Edit Sale" : "Add Sale"}</h2>
              <button style={styles.closeBtn} onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={submit}>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>Bill No</label>
                  <input
                    value={form.bill_no}
                    onChange={(e) => setForm({ ...form, bill_no: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Sale Date</label>
                  <input
                    type="date"
                    value={form.sale_date}
                    onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                    style={styles.input}
                  />
                </div>
                
                <div style={{ ...styles.field, marginBottom: 20, position: 'relative' }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label style={styles.label}>Party</label>
                    <button 
                      type="button" 
                      onClick={() => setShowQuickParty(!showQuickParty)}
                      style={styles.quickAddToggle}
                    >
                      {showQuickParty ? "✕ Cancel" : "+ Add New Party"}
                    </button>
                  </div>

                  {!showQuickParty ? (
                    <div ref={partyDropdownRef} style={{ position: 'relative' }}>
                      <input
                        placeholder="Type to search party..."
                        value={partySearch}
                        onChange={(e) => {
                          setPartySearch(e.target.value);
                          setShowPartyDropdown(true);
                        }}
                        onFocus={() => setShowPartyDropdown(true)}
                        style={styles.input}
                      />
                      
                      {showPartyDropdown && partySearch && (
                        <div style={styles.partyDropdown}>
                          {filteredPartyDropdown.length > 0 ? (
                            filteredPartyDropdown.map((p) => (
                              <div 
                                key={p.id} 
                                style={styles.partyItem}
                                onClick={() => {
                                  setForm({ ...form, party_id: p.id });
                                  setPartySearch(p.name);
                                  setShowPartyDropdown(false);
                                }}
                              >
                                {p.name}
                              </div>
                            ))
                          ) : (
                            <div style={styles.noResult}>No party found</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={styles.quickPartyBox}>
                      <input
                        placeholder="Party Name"
                        value={quickParty.name}
                        onChange={(e) => setQuickParty({ ...quickParty, name: e.target.value })}
                        style={{ ...styles.input, marginBottom: 8 }}
                      />
                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                          placeholder="Phone"
                          value={quickParty.phone}
                          maxLength={10}
                          onChange={(e) => setQuickParty({ ...quickParty, phone: e.target.value })}
                          style={styles.input}
                        />
                        <select
                          value={quickParty.type}
                          onChange={(e) => setQuickParty({ ...quickParty, type: e.target.value })}
                          style={styles.input}
                        >
                          <option value="customer">Customer</option>
                          <option value="supplier">Supplier</option>
                        </select>
                      </div>
                      <input
                        placeholder="Opening Balance"
                        type="number"
                        value={quickParty.balance}
                        onChange={(e) => setQuickParty({ ...quickParty, balance: e.target.value })}
                        style={{ ...styles.input, marginBottom: 8 }}
                      />
                      <button 
                        type="button" 
                        onClick={handleQuickPartySave}
                        style={{ ...styles.primaryBtn, width: "100%", height: 38, fontSize: 13 }}
                      >
                        Save & Select Party
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                {form.items.map((item, index) => (
                  <div key={index} style={{ marginBottom: 15 }}>
                    <div style={styles.itemRow}>
                      <div style={{ position: 'relative' }} ref={activeProductIndex === index ? productDropdownRef : null}>
                        <input
                          placeholder="Search product..."
                          value={productSearch[index] || ""}
                          onChange={(e) => {
                            const newSearch = [...productSearch];
                            newSearch[index] = e.target.value;
                            setProductSearch(newSearch);
                            setActiveProductIndex(index);
                          }}
                          onFocus={() => setActiveProductIndex(index)}
                          style={styles.input}
                        />
                        {activeProductIndex === index && productSearch[index] && (
                          <div style={styles.partyDropdown}>
                            {products
                              .filter(p => p.name.toLowerCase().includes((productSearch[index] || "").toLowerCase()))
                              .map(p => (
                                <div 
                                  key={p.id} 
                                  style={{...styles.partyItem, opacity: p.current_stock <= 0 ? 0.5 : 1}}
                                  onClick={() => {
                                    if(p.current_stock > 0) {
                                        handleItemChange(index, "product_id", p.id);
                                        setActiveProductIndex(null);
                                    }
                                  }}
                                >
                                  {p.name} {p.current_stock <= 0 ? "(Out of Stock)" : `(Stock: ${p.current_stock})`}
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </div>

                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.qty}
                        onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                        style={styles.input}
                      />
                      <input
                        type="number"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                        style={styles.input}
                      />
                      <button
                        type="button"
                        onClick={() => removeItemRow(index)}
                        style={styles.smallBtn}
                        disabled={form.items.length === 1}
                      >Remove</button>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={addItemRow} style={styles.smallBtn}>+ Add Item</button>

              <div style={styles.summaryWrapper}>
                <div style={styles.summaryRow}>
                  <span>Subtotal</span>
                  <strong>₹ {subtotal.toFixed(2)}</strong>
                </div>
                <div style={styles.summaryRow}>
                  <span>Discount (%)</span>
                  <input
                    type="number"
                    value={form.discount_percent}
                    onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Round Off</span>
                  <span style={{color: COLORS.muted}}>{autoRoundOff}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span><strong>Grand Total</strong></span>
                  <strong style={{fontSize: 20, color: COLORS.primary}}>₹ {grandTotal}</strong>
                </div>
                <div style={styles.summaryRow}>
                  <span>Paid Amount</span>
                  <input
                    type="number"
                    value={form.paid_amount}
                    onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                    style={{...styles.summaryInput, borderColor: COLORS.primary, fontWeight: 'bold'}}
                  />
                </div>
                <hr style={{margin: '10px 0', opacity: 0.2}} />
                <div style={styles.summaryRow}>
                  <span>Net Party Balance</span>
                  <strong>₹ {finalBalance.toFixed(2)}</strong>
                </div>
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.actionRow}>
                <button type="button" style={styles.cancelBtn} onClick={resetForm}>Cancel</button>
                <button type="submit" style={styles.primaryBtn}>{editingId ? "Update Sale" : "Save Sale"}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const styles = {
  searchContainer: { marginTop: "12px", width: "300px" },
  searchInput: { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "14px", outline: "none" },
  partyDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '200px', overflowY: 'auto', marginTop: '4px' },
  partyItem: { padding: '10px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: `1px solid #f0f0f0` },
  noResult: { padding: '10px 12px', color: COLORS.muted, fontSize: '13px' },
  page: { background: COLORS.bg, padding: "10px clamp(15px,4vw,30px) 30px", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 30 },
  headerLeft: { display: "flex", flexDirection: "column" },
  title: { fontSize: 28, fontWeight: 700, marginBottom: 6 },
  subtitle: { color: COLORS.muted },
  newBtn: { background: COLORS.primary, color: "#fff", padding: "10px 20px", borderRadius: 10, border: "none", fontWeight: 600, height: 42, cursor: "pointer" },
  card: { background: COLORS.card, borderRadius: 18, padding: "25px 30px" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 850 },
  th: { padding: "14px 12px", textAlign: "left", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  thCenter: { padding: "14px 12px", textAlign: "center", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  td: { padding: "16px 12px", borderBottom: `1px solid ${COLORS.border}` },
  tdBold: { padding: "16px 12px", fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` },
  actionCell: { borderBottom: `1px solid ${COLORS.border}`, textAlign: "center" },
  iconWrapper: { display: "flex", justifyContent: "center", gap: 16 },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer", padding: 4 },
  icon: { width: 18, height: 18 },
  lowBadge: { background: "#FFEBEE", color: COLORS.danger, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  okBadge: { background: "#E3F2FD", color: COLORS.primary, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "flex-end", zIndex: 9999 },
  drawer: { width: "min(600px, 100%)", height: "100vh", background: "#fff", padding: "20px 30px 100px", boxShadow: "-6px 0 20px rgba(0,0,0,0.15)", overflowY: "auto", position: "relative", boxSizing: "border-box" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 25 },
  drawerTitle: { fontSize: 22, fontWeight: 700 },
  closeBtn: { border: "none", background: "none", fontSize: 20, cursor: "pointer" },
  grid: { display: "flex", flexDirection: "column", gap: 18 },
  field: { display: "flex", flexDirection: "column" },
  label: { marginBottom: 6, color: COLORS.muted, fontSize: 13 },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, boxSizing: "border-box" },
  itemRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, alignItems: "center" },
  smallBtn: { padding: "6px 14px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: "pointer" },
  summaryWrapper: { width: "min(320px,100%)", marginLeft: "auto", marginTop: 40, marginBottom: 30 },
  summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  summaryInput: { width: 100, padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, textAlign: "right" },
  actionRow: { display: "flex", justifyContent: "flex-end", marginTop: 30, gap: 12, flexWrap: "wrap" },
  cancelBtn: { padding: "8px 18px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff" },
  primaryBtn: { padding: "8px 20px", borderRadius: 6, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 600 },
  error: { marginTop: 12, color: COLORS.danger },
  quickAddToggle: { border: 'none', background: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  quickPartyBox: { background: "#F8F9FA", padding: 15, borderRadius: 10, border: `1px dashed ${COLORS.border}`, marginTop: 5 }
};

export default Sales;